/**
 * Community Grid Component
 * Displays the 3x3 grid of community cards, with the 8 winning lines shown
 * like fruit-machine paylines — faint dotted guide lines behind the cards,
 * numbered 1-8 around the edge (matching LinePotentials' numbering):
 * 1-3 = rows (down the left side), 4-6 = columns (along the top),
 * 7-8 = diagonals (at the corners they start from). No more per-card
 * position badges — the numbers now live on the paylines themselves.
 *
 * Row/column guides are placed as actual CSS Grid items (self/justify-
 * centered within their own row's/column's track), so they run through the
 * real cell centers exactly, regardless of padding or gap — not a
 * percentage guess. Diagonals still use an SVG (CSS Grid can't do
 * arbitrary angles), confined to the grid's own content box so it isn't
 * thrown off by the outer padding either.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GridCard } from '../../store/types';
import PlayingCard from './PlayingCard';

interface CommunityGridProps {
  cards: GridCard[];
  phase: string;
}

const GUIDE_LINE = 'border-yellow-400/85 pointer-events-none';
const BADGE_BASE =
  'z-20 w-4 h-4 sm:w-5 sm:h-5 rounded-full border flex items-center justify-center text-[9px] sm:text-[10px] font-bold pointer-events-none';
// Primary end of each line: black circle, gold number.
const BADGE = `${BADGE_BASE} bg-black border-yellow-500 text-yellow-300`;
// Mirrored badge at the line's other end: inverted colors (gold circle,
// black number) so either end of a line is unambiguous at a glance.
const BADGE_INVERTED = `${BADGE_BASE} bg-yellow-400 border-black text-black`;

const CommunityGrid: React.FC<CommunityGridProps> = ({ cards, phase }) => {
  // Determine which cards should be highlighted based on phase
  const getHighlightedPositions = (): number[] => {
    switch (phase) {
      case 'ROUND_1':
        return [1, 3, 7, 9]; // Corners
      case 'ROUND_2':
        return [1, 3, 7, 9, 2, 4, 6, 8]; // Corners + Diamond edges
      case 'ROUND_3':
      case 'SHOWDOWN':
        return [1, 2, 3, 4, 5, 6, 7, 8, 9]; // All cards
      default:
        return [];
    }
  };

  const highlightedPositions = getHighlightedPositions();

  const renderCard = (card: GridCard) => {
    const isHighlighted = highlightedPositions.includes(card.position);
    // Explicit grid placement — the payline guides below are also grid
    // items with their own explicit placement (one spans the full 3x3
    // area for the diagonals), which would otherwise push these 9
    // auto-placed cards into extra rows past it.
    const row = Math.ceil(card.position / 3);
    const col = ((card.position - 1) % 3) + 1;

    return (
      // A small inset (on the card elements themselves, not this wrapper)
      // so the behind-the-cards payline guides stay visible in a thin
      // gutter around each card, instead of being fully hidden under
      // opaque card art.
      <div
        key={card.position}
        data-testid="grid-cell"
        data-position={card.position}
        data-revealed={card.revealed ? 'true' : 'false'}
        className="relative aspect-[5/7]"
        style={{ gridRow: row, gridColumn: col }}
      >
        <AnimatePresence mode="wait">
          {/* Seer: the centre seen early, by its holder only — face up, but marked as private. */}
          {!card.revealed && card.card ? (
            <motion.div
              key={`private-${card.position}`}
              data-testid="private-cell"
              className="absolute inset-[6%]"
              initial={{ rotateY: -90 }}
              animate={{ rotateY: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className="relative w-full h-full opacity-80">
                <PlayingCard card={card.card} highlighted={isHighlighted} className="w-full h-full" />
                <div className="absolute inset-0 rounded-lg border-2 border-dashed border-amber-300 pointer-events-none" />
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-amber-300 px-1 text-[8px] font-bold leading-tight text-gray-900">
                Seer only
              </div>
            </motion.div>
          ) : !card.revealed ? (
            // Card back
            <motion.div
              key={`back-${card.position}`}
              className="absolute inset-[6%]"
              initial={{ rotateY: 0 }}
              exit={{ rotateY: 90 }}
              transition={{ duration: 0.3 }}
            >
              <PlayingCard faceDown highlighted={isHighlighted} className="w-full h-full" />
            </motion.div>
          ) : (
            // Card front
            <motion.div
              key={`front-${card.position}`}
              className="absolute inset-[6%]"
              initial={{ rotateY: -90 }}
              animate={{ rotateY: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <PlayingCard card={card.card} highlighted={isHighlighted} className="w-full h-full" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Glow effect for highlighted cards */}
        {isHighlighted && card.revealed && (
          <motion.div
            className="absolute -inset-1 rounded-lg bg-yellow-400/20 blur-md -z-10"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Grid — capped width so it doesn't balloon to fill a wide RHS panel */}
      <div className="relative grid grid-cols-3 grid-rows-3 gap-2 p-5 sm:p-6 max-w-[360px] desk:max-w-none mx-auto bg-gradient-to-br from-green-800 to-green-900 rounded-2xl shadow-2xl border-4 border-yellow-600 overflow-hidden">
        {/* Row guides (1-3) — behind the cards (rendered first, no z-index),
            self-centered within each row's actual track height. Solid, not
            dashed — needs to read clearly, not just as a faint hint. */}
        {[1, 2, 3].map((row) => (
          <div
            key={`row-${row}`}
            className={`col-start-1 col-span-3 self-center h-0 border-t-2 ${GUIDE_LINE}`}
            style={{ gridRow: row }}
          />
        ))}
        {/* Column guides (4-6) */}
        {[1, 2, 3].map((col) => (
          <div
            key={`col-${col}`}
            className={`row-start-1 row-span-3 justify-self-center w-0 border-l-2 ${GUIDE_LINE}`}
            style={{ gridColumn: col }}
          />
        ))}
        {/* Diagonal guides (7-8) — confined to the same grid content box (all 3 rows/cols) so the outer padding doesn't throw them off */}
        <svg
          className="col-start-1 col-span-3 row-start-1 row-span-3 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {/* vectorEffect keeps this at a literal 2 CSS px regardless of
              viewBox scaling, matching the row/column lines' border-*-2
              exactly — without it, the viewBox-to-pixel scaling made these
              render visibly thicker than the CSS-border lines. */}
          <line x1="0" y1="0" x2="100" y2="100" stroke="rgba(250, 204, 21, 0.85)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <line x1="100" y1="0" x2="0" y2="100" stroke="rgba(250, 204, 21, 0.85)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        </svg>

        {cards.map((card) => renderCard(card))}

        {/* Payline numbers — one badge at each end of every line, placed as
            grid items in the SAME row/column as their line (not a
            percentage guess), so they land exactly centered on it. The
            far-end badge uses inverted colors so either end reads clearly. */}
        {[1, 2, 3].map((row) => (
          <div
            key={`rn-${row}`}
            className="col-start-1 col-span-3 flex items-center justify-between"
            style={{ gridRow: row }}
          >
            <div className={`${BADGE} -ml-4 sm:-ml-5`}>{row}</div>
            <div className={`${BADGE_INVERTED} -mr-4 sm:-mr-5`}>{row}</div>
          </div>
        ))}
        {[1, 2, 3].map((col) => (
          <div
            key={`cn-${col}`}
            className="row-start-1 row-span-3 flex flex-col items-center justify-between"
            style={{ gridColumn: col }}
          >
            <div className={`${BADGE} -mt-4 sm:-mt-5`}>{col + 3}</div>
            <div className={`${BADGE_INVERTED} -mb-4 sm:-mb-5`}>{col + 3}</div>
          </div>
        ))}
        {/* Diagonal 7 (↘, positions 1-5-9): badge at both the top-left and bottom-right corners it runs between */}
        <div className="col-start-1 row-start-1 flex items-start justify-start">
          <div className={`${BADGE} -ml-4 -mt-4 sm:-ml-5 sm:-mt-5`}>7</div>
        </div>
        <div className="col-start-3 row-start-3 flex items-end justify-end">
          <div className={`${BADGE_INVERTED} -mr-4 -mb-4 sm:-mr-5 sm:-mb-5`}>7</div>
        </div>
        {/* Diagonal 8 (↗, positions 3-5-7): badge at both the top-right and bottom-left corners it runs between */}
        <div className="col-start-3 row-start-1 flex items-start justify-end">
          <div className={`${BADGE} -mr-4 -mt-4 sm:-mr-5 sm:-mt-5`}>8</div>
        </div>
        <div className="col-start-1 row-start-3 flex items-end justify-start">
          <div className={`${BADGE_INVERTED} -ml-4 -mb-4 sm:-ml-5 sm:-mb-5`}>8</div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGrid;
